import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useNotifier } from "react-headless-notifier";

import useErrorHandler from "hooks/useErrorHandler";
import { useGetBreedQuery, useUpdateBreedMutation } from "api";
import {
  Modal,
  Divider,
  Input,
  Label,
  ReadOnly,
  Tabs,
  FormGroup,
  Button,
  NotifierNotification,
} from "@mwi/ui";

type Inputs = {
  translation: string | undefined;
};

type EditVocabularyModalTabs = "translation";

type EditBreedVocabularyModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const EditBreedVocabularyModal = ({
  isOpen,
  onClose,
}: EditBreedVocabularyModalProps): JSX.Element => {
  const [selectedTab, setSelectedTab] =
    useState<EditVocabularyModalTabs>("translation");

  const { notify } = useNotifier();
  const errorHandler = useErrorHandler();

  const { id } = useParams();

  const {
    data: breed,
    isFetching: fetchingBreed,
    isError: errorFetchingBreed,
  } = useGetBreedQuery(
    {
      breedUuid: id!,
    },
    { skip: !id },
  );

  const [
    updateBreed,
    {
      error: breedErrors,
      isLoading: updatingBreed,
      isSuccess: didUpdateBreed,
      isError: errorUpdatingBreed,
    },
  ] = useUpdateBreedMutation();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState: { errors },
  } = useForm<Inputs>();

  useEffect(() => {
    if (breed) {
      setValue("translation", breed?.translation ?? undefined);
    }
  }, [breed, id]);

  useEffect(() => {
    if (didUpdateBreed) {
      onClose();
      notify(
        <NotifierNotification
          title="Breed Updated"
          message={`${breed?.name} has been updated`}
        />,
      );
    }
  }, [didUpdateBreed]);

  useEffect(() => {
    if (errorUpdatingBreed) {
      errorHandler(breedErrors, setError);
    }

    if (errorFetchingBreed) {
      onClose();
      notify(
        <NotifierNotification
          type="error"
          title="Unable to edit breed"
          message="We're unable to allow breed to be edited at the moment. Please try again later."
        />,
      );
    }
  }, [errorUpdatingBreed, errorFetchingBreed]);

  const handleSave = (data: Inputs) => {
    if (breed) {
      updateBreed({
        breedUuid: breed.id,
        body: {
          enabled: breed.enabled,
          translation: data.translation,
        },
      });
    }
  };

  const handleAfterLeave = () => {
    reset();
    setSelectedTab("translation");
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      loading={fetchingBreed}
      className="flex flex-col tablet:!max-w-[776px]"
      afterLeave={handleAfterLeave}
    >
      <Modal.Header title="Edit Breed" />

      <Modal.Content className="flex flex-col flex-grow">
        {breed && (
          <>
            <Tabs className="gap-x-10">
              <Tabs.Item
                title="Translation"
                isActive={selectedTab === "translation"}
                onClick={() => setSelectedTab("translation")}
              />
            </Tabs>

            <form
              className="flex flex-col justify-between flex-grow mt-12"
              onSubmit={handleSubmit(handleSave)}
            >
              {selectedTab === "translation" && (
                <div className="mb-[228px]">
                  <ReadOnly
                    heading="Breed name"
                    value={breed.name}
                    className="!mb-3"
                  />

                  <Divider className="mb-9" />

                  <FormGroup error={errors.translation?.message}>
                    <Label className="!text-sm !leading-[21px]">
                      Translation
                    </Label>
                    <Input size="small" {...register("translation")} />
                  </FormGroup>
                </div>
              )}

              <Button
                type="submit"
                className="max-w-[153px] mt-4"
                loading={updatingBreed}
              >
                Save
              </Button>
            </form>
          </>
        )}
      </Modal.Content>
    </Modal>
  );
};

export default EditBreedVocabularyModal;
